import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { useCart } from "../../contexts/CartContext";
import { useLessons } from "../../contexts/LessonsContext";
import { useMessages } from "../../contexts/MessagesContext";
import { useDocuments } from "../../contexts/DocumentsContext";
import { useTeachersApproval } from "../../contexts/TeachersApprovalContext";
import {
  SearchIcon,
  HomeIcon,
  LoginIcon,
  LogoutIcon,
  CartIcon,
} from "../common/IconsComponent";
import Badge from "../ui/Badge";
import SpinnerOverlay from "../ui/SpinnerOverlay";

const ROLE_LINKS = {
  admin: [
    { to: "/", label: "דף הבית" },
    { to: "/BankTransfersReport", label: "העברות בנקאיות" },
    { to: "/TchsIncomeReport", label: "הכנסות מורים" },
    { to: "/TchsTaxReport", label: "תשלומי מס" },
    { to: "/ConfirmDocs", label: "מסמכים" },
    { to: "/ConfirmTchrs", label: "אישור מורים" },
    { to: "/GpnBudgetPurchases", label: "רכישות תקציב גפ״ן" },
    { to: "/Profile", label: "פרופיל" },
    { to: "/Messages", label: "הודעות" },
    { to: "/SubPhotos", label: "תמונות קורסים" },
  ],
  teacher: [
    { to: "/", label: "דף הבית" },
    { to: "/MyCourses", label: "הקורסים שלי" },
    { to: "/OnlineClass", label: "כיתה מקוונת" },
    { to: "/PrivateLessonsRequests", label: "בקשות שיעור" },
    { to: "/DoneLessons", label: "דוח הכנסות" },
    { to: "/Profile", label: "פרופיל" },
    { to: "/Contact", label: "צור קשר" },
    { to: "/Help", label: "עזרה" },
  ],
  student: [
    { to: "/", label: "דף הבית" },
    { to: "/MyCourses", label: "הקורסים שלי" },
    { to: "/OnlineClass", label: "כיתה מקוונת" },
    { to: "/RedeemCoupon", label: "מימוש קופון גפ״ן" },
    { to: "/MyWallet", label: "הארנק שלי" },
    { to: "/Profile", label: "פרופיל" },
    { to: "/Contact", label: "צור קשר" },
    { to: "/Cart", label: "סל שיעורים" },
    { to: "/Help", label: "עזרה" },
    { to: "/Search", label: "חיפוש" },
  ],
  // Learning-center / supplier role. Mirrors the Supplier Area sidebar
  // (Dashboard, Courses, Teachers, Lessons, GEFEN Programs, Revenue),
  // trimmed to the items worth one click from anywhere in the app —
  // Students, Schools, Coupons, Refunds, Reports and Settings live
  // inside the Supplier Area itself rather than the top header.
  supplier: [
    { to: "/", label: "דף הבית" },
    { to: "/supplier/dashboard", label: "לוח בקרה" },
    { to: "/supplier/courses", label: "קורסים" },
    { to: "/supplier/teachers", label: "מורים" },
    { to: "/OnlineClass", label: "שיעורים / מפגשים" },
    { to: "/supplier/programs", label: "תוכניות גפ״ן" },
    { to: "/supplier/revenue", label: "הכנסות" },
    { to: "/supplier/revenue/teacher", label: "דוח לפי מורה" },
    { to: "/Profile", label: "פרופיל" },
    { to: "/Contact", label: "צור קשר" },
  ],
  // School-administrator role. Mirrors the School Area sidebar
  // (Dashboard, GEFEN Programs) — coupon issuance, budget purchase and
  // student rosters live inside the programs page itself rather than
  // getting their own top-level header links.
  school: [
    { to: "/", label: "דף הבית" },
    { to: "/school/dashboard", label: "לוח בקרה" },
    { to: "/school/programs", label: "תוכניות גפ״ן" },
    { to: "/Profile", label: "פרופיל" },
    { to: "/Contact", label: "צור קשר" },
    { to: "/Help", label: "עזרה" },
  ],
  default: [
    { to: "/", label: "דף הבית" },
    { to: "/About", label: "אודות" },
    { to: "/Contact", label: "צור קשר" },
    { to: "/Help", label: "עזרה" },
    { to: "/Search", label: "חיפוש" },
  ],
};

const CountDot = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="absolute -top-1 -start-1 min-w-[18px] h-[18px] px-0.5 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center font-bold leading-none">
      {count > 99 ? "99+" : count}
    </span>
  );
};

const Header = ({ mobileMenuOpen, setMobileMenuOpen, burgerRef }) => {
  const location = useLocation();
  const { logout, role, user, isLoading } = useUser();

  const { counts: msgCounts } = useMessages();
  const { adminCounts, loadAdminCounts } = useDocuments();
  const { counts: teachersCounts, loadTeachersCounts } = useTeachersApproval();

  const { lessons: cartLessons } = useCart();
  const { pendingPrivateCount } = useLessons();

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isLoggedIn = role !== "default";
  const cartCount = cartLessons.length;

  useEffect(() => {
    if (role === "admin") {
      loadAdminCounts();
      loadTeachersCounts();
    }
  }, [role, loadAdminCounts, loadTeachersCounts]);

  const handleLogout = async () => {
    if (isLoggingOut || isLoading) return;

    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
      setMobileMenuOpen?.(false);
    }
  };

  const badgeCounts = {
    "/Messages": Number(msgCounts?.globalUnread || 0),
    "/ConfirmDocs": Number(adminCounts?.NO_RESPONSE || 0),
    "/ConfirmTchrs": Number(teachersCounts?.PENDING || 0),
    "/PrivateLessonsRequests": Number(pendingPrivateCount || 0),
    // A supplier's "Lessons / Meetings" link now points at the same
    // /OnlineClass path teachers and students already use for their
    // own nav item — badgeCounts is a flat path-keyed lookup with no
    // role-awareness, so keying this by /OnlineClass would incorrectly
    // surface pendingPrivateCount on teacher's and student's identical
    // link too. Dropped rather than risk that; giving suppliers their
    // own badge here would need badgeCounts (and renderDesktopLink's
    // lookup) to become role-aware, not just path-aware.
  };

  const links = ROLE_LINKS[role] ?? ROLE_LINKS.default;

  const renderDesktopLink = (item) => {
    const isActive = location.pathname === item.to;
    const badge = badgeCounts[item.to] ?? 0;

    if (item.to === "/") {
      return (
        <Link
          key={item.to}
          to={item.to}
          title={item.label}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center hover:bg-indigo-50 rounded-full transition"
        >
          <HomeIcon className="w-5 h-5 text-indigo-600" />
        </Link>
      );
    }

    if (item.to === "/Search") {
      return (
        <Link
          key={item.to}
          to={item.to}
          title={item.label}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center hover:bg-indigo-50 rounded-full transition"
        >
          <SearchIcon className="w-5 h-5 text-indigo-600" />
        </Link>
      );
    }

    if (item.to === "/Cart") {
      return (
        <Link
          key={item.to}
          to={item.to}
          title={item.label}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center hover:bg-indigo-50 rounded-full transition"
        >
          <Badge count={cartCount}>
            <CartIcon className="w-5 h-5 text-indigo-600" />
          </Badge>
        </Link>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        className={`relative whitespace-nowrap text-[0.78rem] lg:text-sm font-medium px-2 py-1.5 lg:px-3 lg:py-2 rounded-lg transition flex-shrink-0 ${
          isActive
            ? "text-indigo-700 bg-indigo-50"
            : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50"
        }`}
      >
        {item.label}
        <CountDot count={badge} />
      </Link>
    );
  };

  const Avatar = () => (
    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-indigo-200 flex-shrink-0">
      {user?.picture ? (
        <img src={user.picture} alt="פרופיל" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-indigo-100 text-indigo-700 font-bold text-sm">
          {user?.firstName?.charAt(0) || "א"}
        </div>
      )}
    </div>
  );

  return (
    <>
      {(isLoggingOut || isLoading ) && (
        <SpinnerOverlay
          variant="glow"
          text="נא להמתין"
          dark={false}
          blur={false}
        />
      )}

      <header className="bg-white border-b border-gray-200 shadow-sm top-0 z-30" dir="rtl">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 min-h-14 sm:min-h-16 py-2 flex items-start justify-between gap-2">
          <span className="order-last self-center text-lg sm:text-xl font-bold bg-gradient-to-l from-indigo-700 to-purple-500 text-transparent bg-clip-text whitespace-nowrap flex-shrink-0">
            DoroosOnline
          </span>

          <div className="hidden md:flex items-center gap-1.5 flex-1 min-w-0 py-1">
            <Avatar />

            <nav className="flex items-center flex-wrap gap-x-0.5 gap-y-1 flex-1 min-w-0">
              {links.map(renderDesktopLink)}

              {isLoggedIn ? (
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut || isLoading}
                  className={`flex items-center gap-1.5 px-2 py-1.5 lg:px-3 rounded-lg transition text-[0.78rem] lg:text-sm font-medium flex-shrink-0 whitespace-nowrap ${
                    isLoggingOut || isLoading
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-red-50 text-red-600 hover:bg-red-100"
                  }`}
                >
                  <LogoutIcon className="w-4 h-4" />
                  התנתקות
                </button>
              ) : (
                <Link
                  to="/login_page"
                  className="flex items-center gap-1.5 bg-indigo-50 text-indigo-600 px-2 py-1.5 lg:px-3 rounded-lg hover:bg-indigo-100 transition text-[0.78rem] lg:text-sm font-medium flex-shrink-0 whitespace-nowrap"
                >
                  <LoginIcon className="w-4 h-4" />
                  התחברות
                </Link>
              )}
            </nav>
          </div>

          <div className="flex md:hidden items-center gap-4 self-center">
            <button
              ref={burgerRef}
              onClick={(e) => {
                e.stopPropagation();
                setMobileMenuOpen((p) => !p);
              }}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-indigo-600 transition"
              aria-label="תפריט"
            >
              {mobileMenuOpen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            {role === "student" && (
              <Link to="/Cart" className="relative w-8 h-8 flex items-center justify-center">
                <CartIcon className="w-5 h-5 text-indigo-600" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center font-bold">
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </Link>
            )}

            {(role === "student" || role === "default") && (
              <Link to="/Search" className="w-8 h-8 flex items-center justify-center">
                <SearchIcon className="w-5 h-5 text-indigo-600" />
              </Link>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;